import React, { useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuditSnapshots, useRestoreEvents } from '../../modules/audit/hooks';
import type {
  AuditSnapshot,
  RestoreEvent,
  RestoreStatus,
  SnapshotType,
} from '../../modules/audit/models';
import {
  Database,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Upload,
  RefreshCw,
  Shield,
  History,
  Eye,
  Plus,
  FileText,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// ─── constants ───────────────────────────────────────────────────────────────

const SNAPSHOT_TYPE_LABELS: Record<SnapshotType, string> = {
  full_schema: 'Full Schema',
  rls_policies: 'RLS Policies',
  paypal_config: 'PayPal Config',
  user_roles: 'User Roles',
  billing_state: 'Billing State',
};

const SNAPSHOT_TYPE_COLORS: Record<SnapshotType, string> = {
  full_schema: 'bg-blue-100 text-blue-800',
  rls_policies: 'bg-orange-100 text-orange-800',
  paypal_config: 'bg-green-100 text-green-800',
  user_roles: 'bg-yellow-100 text-yellow-800',
  billing_state: 'bg-pink-100 text-pink-800',
};

const RESTORE_STATUS_STYLES: Record<RestoreStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-cyan-100 text-cyan-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const RESTORE_STATUS_LABELS: Record<RestoreStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const ALL_FILTER = 'all';
type FilterValue = typeof ALL_FILTER | SnapshotType;

const FILTER_TABS: { label: string; value: FilterValue }[] = [
  { label: 'All', value: ALL_FILTER },
  { label: 'Full Schema', value: 'full_schema' },
  { label: 'RLS Policies', value: 'rls_policies' },
  { label: 'PayPal Config', value: 'paypal_config' },
  { label: 'User Roles', value: 'user_roles' },
  { label: 'Billing State', value: 'billing_state' },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(str: string | null | undefined, len: number): string {
  if (!str) return '—';
  return str.length <= len ? str : str.slice(0, len) + '…';
}

// ─── sub-components ──────────────────────────────────────────────────────────

function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-10 w-10' : 'h-6 w-6';
  return (
    <RefreshCw
      className={`${sz} animate-spin text-[#40E0D0]`}
      aria-label="Loading"
    />
  );
}

function EmptyState({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Icon className="h-7 w-7 text-gray-400" />
      </div>
      <p className="text-gray-700 font-medium">{title}</p>
      {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
    </div>
  );
}

function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all
        ${type === 'success' ? 'bg-[#40E0D0]/10 border border-[#40E0D0]/30 text-[#20a898]' : 'bg-red-50 border border-red-200 text-red-700'}`}
    >
      {type === 'success' ? (
        <CheckCircle className="h-4 w-4 text-[#40E0D0] flex-shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
      )}
      {message}
      <button onClick={onClose} className="ml-2 text-gray-400 hover:text-gray-600">
        <XCircle className="h-4 w-4" />
      </button>
    </div>
  );
}

// ─── Integrity Check expandable ──────────────────────────────────────────────

function IntegrityCheckPanel({ data }: { data: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-1.5 text-xs text-[#40E0D0] hover:text-[#20a898] font-medium"
      >
        <Eye className="h-3.5 w-3.5" />
        {expanded ? 'Hide' : 'View'} Integrity Check
      </button>
      {expanded && (
        <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs font-mono space-y-1">
          {Object.entries(data).map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="text-gray-500 min-w-[140px] flex-shrink-0">{k}:</span>
              <span className="text-gray-800 break-all">{JSON.stringify(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Capture Snapshot Modal ───────────────────────────────────────────────────

interface CaptureModalProps {
  onClose: () => void;
  onCreate: (data: Omit<AuditSnapshot, 'id' | 'created_at'>) => Promise<AuditSnapshot>;
  userId: string;
  onSuccess: () => void;
}

function CaptureSnapshotModal({ onClose, onCreate, userId, onSuccess }: CaptureModalProps) {
  const [form, setForm] = useState({
    snapshot_type: 'full_schema' as SnapshotType,
    description: '',
    environment: 'production',
    is_baseline: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSaving(true);
      setError(null);
      try {
        await onCreate({
          snapshot_type: form.snapshot_type,
          description: form.description || null,
          environment: form.environment || null,
          is_baseline: form.is_baseline,
          created_by: userId,
          snapshot_data: {},
          integrity_hash: 'pending',
        });
        onSuccess();
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setSaving(false);
      }
    },
    [form, onCreate, userId, onSuccess, onClose]
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-[#40E0D0]" />
            <h2 className="text-base font-semibold text-gray-900">Capture Snapshot</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Snapshot Type</label>
            <select
              value={form.snapshot_type}
              onChange={e => setForm(f => ({ ...f, snapshot_type: e.target.value as SnapshotType }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0]"
              required
            >
              {(Object.keys(SNAPSHOT_TYPE_LABELS) as SnapshotType[]).map(t => (
                <option key={t} value={t}>
                  {SNAPSHOT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
            <input
              type="text"
              value={form.environment}
              onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0]"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_baseline}
              onChange={e => setForm(f => ({ ...f, is_baseline: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-[#40E0D0] focus:ring-[#40E0D0]/40"
            />
            <span className="text-sm text-gray-700">Mark as baseline</span>
          </label>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#40E0D0] hover:bg-[#2bc9b9] rounded-lg transition-colors disabled:opacity-60"
            >
              {saving ? <LoadingSpinner size="sm" /> : <Download className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Capture'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Restore Workflow Modal ────────────────────────────────────────────────────

type RestoreStep = 'preview' | 'confirm' | 'token' | 'done';

interface RestoreModalProps {
  snapshot: AuditSnapshot;
  onClose: () => void;
  onCreate: (data: Omit<RestoreEvent, 'id' | 'created_at'>) => Promise<RestoreEvent>;
  onUpdateStatus: (id: string, status: RestoreStatus) => Promise<void>;
  userId: string;
}

function RestoreWorkflowModal({ snapshot, onClose, onCreate, onUpdateStatus, userId }: RestoreModalProps) {
  const [step, setStep] = useState<RestoreStep>('preview');
  const [reason, setReason] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  const [confirmationToken] = useState(() => `RESTORE-${Date.now().toString(36).toUpperCase()}`);
  const [restoreEventId, setRestoreEventId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!reason.trim()) return;
      setLoading(true);
      setError(null);
      try {
        const evt = await onCreate({
          restore_type: snapshot.snapshot_type,
          snapshot_id: snapshot.id,
          initiated_by: userId,
          reason: reason.trim(),
          confirmation_token: confirmationToken,
          status: 'pending',
          pre_restore_snapshot_id: null,
          post_restore_integrity_check: null,
          completed_at: null,
        });
        setRestoreEventId(evt.id);
        setStep('token');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [reason, confirmationToken, snapshot, userId, onCreate]
  );

  const handleExecute = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (tokenInput !== confirmationToken || !restoreEventId) return;
      setLoading(true);
      setError(null);
      try {
        await onUpdateStatus(restoreEventId, 'confirmed');
        setStep('done');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [tokenInput, confirmationToken, restoreEventId, onUpdateStatus]
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#40E0D0]" />
            <h2 className="text-base font-semibold text-gray-900">
              {step === 'done' ? 'Restore Initiated' : 'Restore Snapshot'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Step 0: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Snapshot Details</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Type:</span> <span className="font-medium text-gray-800">{SNAPSHOT_TYPE_LABELS[snapshot.snapshot_type]}</span></div>
                  <div><span className="text-gray-500">Environment:</span> <span className="font-medium text-gray-800">{snapshot.environment ?? '—'}</span></div>
                  <div><span className="text-gray-500">Created:</span> <span className="font-medium text-gray-800">{formatDate(snapshot.created_at)}</span></div>
                  <div><span className="text-gray-500">Hash:</span> <span className="font-mono text-xs text-gray-700">{snapshot.integrity_hash.slice(0, 16)}</span></div>
                  <div><span className="text-gray-500">Baseline:</span> <span className="font-medium text-gray-800">{snapshot.is_baseline ? 'Yes' : 'No'}</span></div>
                </div>
                {snapshot.description && (
                  <p className="text-sm text-gray-700 border-t border-gray-200 pt-2">{snapshot.description}</p>
                )}
              </div>

              {snapshot.snapshot_data && Object.keys(snapshot.snapshot_data).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Snapshot Data Preview</p>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg px-4 py-3 text-xs overflow-x-auto whitespace-pre-wrap break-all max-h-48">
                    {JSON.stringify(snapshot.snapshot_data, null, 2)}
                  </pre>
                </div>
              )}

              <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5">
                <Eye className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">Review the snapshot above before proceeding. Once confirmed, the restore will be logged permanently.</p>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
                <button type="button" onClick={() => setStep('confirm')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#40E0D0] hover:bg-[#2bc9b9] rounded-lg transition-colors">
                  <Shield className="h-4 w-4" />
                  Continue to Restore
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Confirm */}
          {step === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-4">
              {/* Snapshot summary */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SNAPSHOT_TYPE_COLORS[snapshot.snapshot_type]}`}>
                    {SNAPSHOT_TYPE_LABELS[snapshot.snapshot_type]}
                  </span>
                  {snapshot.is_baseline && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#40E0D0]/15 text-[#20a898]">
                      Baseline
                    </span>
                  )}
                </div>
                {snapshot.description && (
                  <p className="text-sm text-gray-700">{snapshot.description}</p>
                )}
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div>Created: {formatDate(snapshot.created_at)}</div>
                  <div>Environment: {snapshot.environment ?? '—'}</div>
                  <div>Hash: <span className="font-mono">{snapshot.integrity_hash.slice(0, 8)}</span></div>
                </div>
              </div>

              {/* Warning */}
              <div className="flex gap-2 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  This action will restore the system to this snapshot state. Proceed with caution.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  placeholder="Describe why this restore is necessary…"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0] resize-none"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !reason.trim()}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#40E0D0] hover:bg-[#2bc9b9] rounded-lg transition-colors disabled:opacity-60"
                >
                  {loading ? <LoadingSpinner size="sm" /> : <Shield className="h-4 w-4" />}
                  Confirm Restore
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Token confirmation */}
          {step === 'token' && (
            <form onSubmit={handleExecute} className="space-y-4">
              <p className="text-sm text-gray-600">
                A restore event has been created. To execute, type the confirmation token exactly as shown below.
              </p>

              <div className="rounded-xl bg-gray-900 px-4 py-3 text-center">
                <span className="font-mono text-[#40E0D0] text-base tracking-widest select-all">
                  {confirmationToken}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type the token to proceed
                </label>
                <input
                  type="text"
                  value={tokenInput}
                  onChange={e => setTokenInput(e.target.value)}
                  placeholder={confirmationToken}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#40E0D0]/40 focus:border-[#40E0D0]"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || tokenInput !== confirmationToken}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#40E0D0] hover:bg-[#2bc9b9] rounded-lg transition-colors disabled:opacity-60"
                >
                  {loading ? <LoadingSpinner size="sm" /> : <Upload className="h-4 w-4" />}
                  Execute Restore
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center py-6 text-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#40E0D0]/15 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-[#40E0D0]" />
              </div>
              <div>
                <p className="text-base font-semibold text-gray-900">Restore Confirmed</p>
                <p className="text-sm text-gray-500 mt-1">
                  The restore event has been created and confirmed. Monitor the restore history for status updates.
                </p>
                <p className="text-xs text-gray-400 mt-1 font-mono">Token: {confirmationToken}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const report = [
                      '# Restore Report',
                      `Date: ${new Date().toISOString()}`,
                      `Snapshot ID: ${snapshot.id}`,
                      `Snapshot Type: ${snapshot.snapshot_type}`,
                      `Environment: ${snapshot.environment ?? 'unknown'}`,
                      `Confirmation Token: ${confirmationToken}`,
                      `Reason: ${reason}`,
                      `Initiated By: ${userId}`,
                      `Integrity Hash: ${snapshot.integrity_hash}`,
                    ].join('\n');
                    const blob = new Blob([report], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `restore-report-${Date.now()}.txt`; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <FileText className="h-4 w-4" />
                  Download Report
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2 text-sm font-medium text-white bg-[#40E0D0] hover:bg-[#2bc9b9] rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Snapshot Card ────────────────────────────────────────────────────────────

interface SnapshotCardProps {
  snapshot: AuditSnapshot;
  onRestore: (snapshot: AuditSnapshot) => void;
}

function SnapshotCard({ snapshot, onRestore }: SnapshotCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SNAPSHOT_TYPE_COLORS[snapshot.snapshot_type]}`}>
            {SNAPSHOT_TYPE_LABELS[snapshot.snapshot_type]}
          </span>
          {snapshot.is_baseline && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#40E0D0]/15 text-[#20a898]">
              Baseline
            </span>
          )}
        </div>
        <Database className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
      </div>

      {snapshot.description && (
        <p className="text-sm text-gray-700 leading-snug">{snapshot.description}</p>
      )}

      <div className="text-xs text-gray-500 space-y-1">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          {formatDate(snapshot.created_at)}
        </div>
        {snapshot.environment && (
          <div className="flex items-center gap-1.5">
            <Shield className="h-3 w-3" />
            {snapshot.environment}
          </div>
        )}
        <div className="font-mono text-gray-400">
          #{snapshot.integrity_hash.slice(0, 8)}
        </div>
      </div>

      <button
        onClick={() => onRestore(snapshot)}
        className="mt-auto flex items-center justify-center gap-1.5 w-full py-2 text-sm font-medium text-[#40E0D0] border border-[#40E0D0]/40 rounded-lg hover:bg-[#40E0D0]/10 transition-colors"
      >
        <Upload className="h-4 w-4" />
        Restore
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SecurityRecovery() {
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState<FilterValue>(ALL_FILTER);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<AuditSnapshot | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const { snapshots, loading: snapshotsLoading, error: snapshotsError, createSnapshot } =
    useAuditSnapshots(activeFilter === ALL_FILTER ? undefined : activeFilter);

  const { restoreEvents, loading: eventsLoading, error: eventsError, createRestoreEvent, updateStatus } =
    useRestoreEvents();

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const userId = user?.id ?? '';

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto space-y-10">

        {/* ── Section 1: Snapshot Browser ── */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-[#40E0D0]" />
              <h2 className="text-lg font-semibold text-gray-900">System Snapshots</h2>
            </div>
            <button
              onClick={() => setShowCaptureModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#40E0D0] hover:bg-[#2bc9b9] rounded-lg transition-colors shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Capture Snapshot
            </button>
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-1.5 mb-5">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveFilter(tab.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === tab.value
                    ? 'bg-[#40E0D0] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Snapshot grid */}
          {snapshotsLoading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : snapshotsError ? (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <XCircle className="h-4 w-4 flex-shrink-0" />
              {snapshotsError}
            </div>
          ) : snapshots.length === 0 ? (
            <EmptyState
              icon={Database}
              title="No snapshots found"
              subtitle="Capture a snapshot to get started"
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {snapshots.map(s => (
                <SnapshotCard
                  key={s.id}
                  snapshot={s}
                  onRestore={setRestoreTarget}
                />
              ))}
            </div>
          )}
        </section>

        {/* ── Section 2: Restore History ── */}
        <section>
          <div className="flex items-center gap-2 mb-5">
            <History className="h-5 w-5 text-[#40E0D0]" />
            <h2 className="text-lg font-semibold text-gray-900">Restore Events</h2>
          </div>

          {eventsLoading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : eventsError ? (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <XCircle className="h-4 w-4 flex-shrink-0" />
              {eventsError}
            </div>
          ) : restoreEvents.length === 0 ? (
            <EmptyState
              icon={History}
              title="No restore events"
              subtitle="Restore history will appear here"
            />
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left">
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Snapshot ID</th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 font-medium text-gray-600">Reason</th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Initiated By</th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Created At</th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Completed At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {restoreEvents.map(evt => (
                      <React.Fragment key={evt.id}>
                        <tr className="hover:bg-gray-50/60 transition-colors align-top">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                              {evt.restore_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-500">
                            {truncate(evt.snapshot_id, 8)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${RESTORE_STATUS_STYLES[evt.status]}`}>
                              {RESTORE_STATUS_LABELS[evt.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-700 max-w-[200px]">
                            <span title={evt.reason}>{truncate(evt.reason, 60)}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-500">
                            {truncate(evt.initiated_by, 8)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                            {formatDate(evt.created_at)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                            {formatDate(evt.completed_at)}
                          </td>
                        </tr>
                        {evt.post_restore_integrity_check &&
                          Object.keys(evt.post_restore_integrity_check).length > 0 && (
                            <tr className="bg-gray-50/40">
                              <td colSpan={7} className="px-4 pb-3">
                                <IntegrityCheckPanel data={evt.post_restore_integrity_check} />
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── Modals ── */}
      {showCaptureModal && (
        <CaptureSnapshotModal
          onClose={() => setShowCaptureModal(false)}
          onCreate={createSnapshot}
          userId={userId}
          onSuccess={() => showToast('Snapshot captured successfully.', 'success')}
        />
      )}

      {restoreTarget && (
        <RestoreWorkflowModal
          snapshot={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onCreate={createRestoreEvent}
          onUpdateStatus={updateStatus}
          userId={userId}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminLayout>
  );
}
